import { useState } from 'react'
import PropTypes from 'prop-types'
import { useAtom } from 'jotai'

import { AddCartIcon } from '@!/components/Icon'
import { CircularProgressModal, ConfirmPopupModal } from '@!/components/Modal'
import { userHashAtom } from '@!/atoms/userInfoAtom'
import { cartItemsAtom } from '@!/atoms/cartItemsAtom'
import { saveCartItemsLoggedIn, saveCartItemsNotLoggedIn } from '@!/Util/localStorageData'
import { Config } from '@!/config/config'
import { useCreateCartItems } from '@!/Hooks'
import { SecondaryButton } from '@!/components/Button'
import { volumesApi } from '@!/Api/storeBook'
import { checkPurchasedBooksApi } from '@!/Api/user'
import { sliceByNumber } from '@!/lib/array'

export const AddAllVolumeButton = ({ bookTitleId, buttonText }) => {
  const [cartItems, setCartItems] = useAtom(cartItemsAtom)
  const [userHash] = useAtom(userHashAtom)
  const [showConfirmPopup, setShowConfirmPopup] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [redirectToCart, setRedirectToCart] = useState(false)
  const [createCartItemsMutation] = useCreateCartItems(userHash)
  const [isLoading, setIsLoading] = useState(false)

  const maximumNumberOfCarts = userHash ? Config.maximumNumberOfCartsLoggedIn : Config.maximumNumberOfCartsNotLoggedIn

  const getFilterdVolumes = (volumes, purchasedVolumeIds) => {
    // 購入済み作品を除外して全巻一覧を返す
    const volumesWithPurchaseStatus = volumes.map((volume) => ({
      ...volume,
      purchased: purchasedVolumeIds.some((purchasedBookId) => purchasedBookId == volume.content_id),
    }))
    return volumesWithPurchaseStatus.filter((volume) => !volume.purchased)
  }

  const buildNewCartItems = (filteredVolumes) => {
    // 既にカートに存在する作品と重複するものを除外
    const volumesToAdd = filteredVolumes.filter((volume) => !cartItems.some((cartItem) => cartItem.content_id === volume.content_id))

    return [...cartItems, ...volumesToAdd]
  }

  const execCartApi = (cartItems) => {
    const createCartItemsParams = cartItems.map((item) => {
      return {
        content_id: item.content_id,
      }
    })
    createCartItemsMutation.mutate(
      { contents: createCartItemsParams },
      {
        onSuccess: () => {
          saveCartItems(cartItems)
        },
        onError: () => {
          setRedirectToCart(false)
          openConfirmPopup('カートへの追加に失敗しました。')
        },
      },
    )
  }

  const saveCartItemsToLocalStorage = (cartItems) => {
    if (userHash) {
      saveCartItemsLoggedIn(cartItems)
    } else {
      saveCartItemsNotLoggedIn(cartItems)
    }
  }

  const saveCartItems = (newCartItems) => {
    saveCartItemsToLocalStorage(newCartItems)
    setCartItems(newCartItems)
    setRedirectToCart(true)
    openConfirmPopup('カートに追加しました')
  }

  const openConfirmPopup = (message) => {
    setConfirmMessage(message)
    setShowConfirmPopup(true)
  }

  const fetchPurchasedVolumeIds = async (contentIds) => {
    const splitedContentIds = sliceByNumber(contentIds, 50)
    const purchasedIds = await Promise.all(splitedContentIds.map(async (ids) => await checkPurchasedBooksApi(ids)))
    return purchasedIds.flat()
  }

  const addAllVolumeToCart = async (event) => {
    event.preventDefault()
    setIsLoading(true)

    try {
      const volumes = await volumesApi(bookTitleId)

      if (!volumes.length) {
        openConfirmPopup('カートへの追加に失敗しました。')
        return
      }

      let filteredVolumes = volumes
      let purchasedVolumeIds = []

      if (userHash) {
        const contentIds = volumes.map((volume) => volume.content_id)
        purchasedVolumeIds = await fetchPurchasedVolumeIds(contentIds)
        filteredVolumes = getFilterdVolumes(volumes, purchasedVolumeIds)
      }

      const newCartItems = buildNewCartItems(filteredVolumes, purchasedVolumeIds)

      if (!newCartItems.length) {
        openConfirmPopup('新しく購入できる巻がありません。')
        return
      }

      // 追加後のカート数と最大登録可能件数の比較
      if (newCartItems.length > maximumNumberOfCarts) {
        openConfirmPopup(`カートに追加できるのは最大${maximumNumberOfCarts}件です。`)
        return
      }

      if (userHash) {
        // ログイン時はAPI実行
        execCartApi(newCartItems)
        return
      }

      // 未ログイン時はLocal Storageとatomを更新
      saveCartItems(newCartItems)
    } catch (error) {
      console.log(error)
      openConfirmPopup('カートへの追加に失敗しました。')
      return
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <SecondaryButton width="100%" onClick={addAllVolumeToCart} rightIcon={<AddCartIcon width="24px" height="24px" ml={-1} />}>
        {buttonText}
      </SecondaryButton>
      <CircularProgressModal isOpen={createCartItemsMutation.isPending || isLoading} />
      <ConfirmPopupModal
        text={confirmMessage}
        isOpen={showConfirmPopup}
        handlePopup={() => {
          setShowConfirmPopup(false)
          if (redirectToCart) {
            window.location.href = '/cart'
          }
        }}
      />
    </>
  )
}

AddAllVolumeButton.propTypes = {
  bookTitleId: PropTypes.number.isRequired,
  buttonText: PropTypes.string.isRequired,
}
